import { IconType, H5 } from "@sellar-io/components";
import { ReactNode } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { ACCOUNT_NOTIFICATION_SETTINGS } from "../../constants/routes";
import { useAnnouncementList } from "../../hooks/api/v2/announcement/useAnnouncementList";
import { useAnnouncementMarkAllAsRead } from "../../hooks/api/v2/announcement/useAnnouncementMarkAllAsRead";
import { useNotificationList } from "../../hooks/api/v2/notification/useNotificationList";
import { useNotificationMarkAllAsRead } from "../../hooks/api/v2/notification/useNotificationMarkAllAsRead";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";

import { Icon } from "@sellar-io/components";
import { InfiniteList } from "../infiniteLoading/InfiniteList";
import { SpinnerLegacy } from "..";
import { NotificationAnnouncementGroup } from "./NotificationAnnouncementGroup";
import { NotificationGroup } from "./NotificationGroup";
import { NotificationTabs } from "./NotificationTabs";

const getNotificationCount = (count: number) => {
  if (count >= 10) {
    return "10+";
  }

  return count;
};

const getButtonText = ({ isLoading }: { isLoading: boolean }) => {
  if (isLoading) {
    return "Marking...";
  }

  return "Mark all as read";
};

export const NotificationList = ({
  setPopoverActive,
  children,
  isNotificationPopover,
}: {
  setPopoverActive?: (active: boolean) => void;
  isNotificationPopover: boolean;
  children?: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const { isNonMemberAdmin } = useSellarCurrentBusiness();
  const markAllAsReadMutation = useNotificationMarkAllAsRead();
  // Get today's notifications
  const recentNotificationsQuery = useNotificationList({
    query: [{ type: "today", value: true, modifier: "EQUALS" }],
    limit: 10,
  });

  // Get older notifications (older than today)
  const olderNotificationsQuery = useNotificationList({
    query: [{ type: "today", value: false, modifier: "EQUALS" }],
    limit: 10,
  });

  const recentNotificationsLength =
    recentNotificationsQuery.data?.pages[0]?.length || 0;
  const olderNotificationsLength =
    olderNotificationsQuery.data?.pages[0]?.length || 0;
  const isLoading =
    olderNotificationsQuery.isPending || recentNotificationsQuery.isPending;
  return (
    <>
      <div className="flex flex-col items-start justify-start gap-4 self-stretch p-4">
        <div className="flex flex-col items-start justify-start gap-4 self-stretch">
          {isNotificationPopover && (
            <div className="inline-flex items-center justify-between self-stretch">
              <div className="relative h-6 w-6 overflow-hidden opacity-0">
                <div className="absolute top-[2.40px] left-[2.67px] h-5 w-5 bg-zinc-800" />
              </div>
              <div className="justify-end text-base leading-normal font-semibold text-zinc-800">
                Notifications
              </div>
              <Icon
                className={"cursor-pointer"}
                icon={"COG"}
                onClick={() => {
                  navigate(ACCOUNT_NOTIFICATION_SETTINGS, {
                    state: {
                      source: "Notifications tray",
                    },
                  });
                  setPopoverActive?.(false);
                }}
              />
            </div>
          )}
          <NotificationTabs
            showSettings={!isNotificationPopover}
            isNotificationPopover={isNotificationPopover}
            mixpanelEventName={
              isNotificationPopover
                ? "Notification Tray Viewed"
                : "Notification Page Viewed"
            }
            tab1={
              <>
                {isNonMemberAdmin ? (
                  <NotificationListEmptyState
                    icon="BELL_OUTLINE"
                    title="Viewing as an admin"
                    message="You're viewing this business as an admin. Personal notifications are only available to members of this business."
                  />
                ) : isLoading ? (
                  <div
                    className={
                      "flex flex-row items-center justify-between rounded-lg bg-gray-100"
                    }
                  >
                    <SpinnerLegacy wrapperClassNames={"m-0 p-0"} />
                  </div>
                ) : (
                  <>
                    {!!recentNotificationsLength ||
                    !!olderNotificationsLength ? (
                      <div className="mt-2 flex flex-col items-start justify-start gap-4">
                        <div
                          className={twMerge(
                            "flex w-full flex-col items-start justify-start gap-2",
                          )}
                        >
                          <div className="inline-flex items-center justify-between self-stretch">
                            <div className="justify-end text-sm leading-tight font-normal text-zinc-700">
                              Today (
                              {getNotificationCount(recentNotificationsLength)})
                            </div>
                            <div
                              className="cursor-pointer justify-end text-base leading-normal font-semibold text-zinc-800"
                              onClick={() => {
                                markAllAsReadMutation.mutate({});
                              }}
                            >
                              {getButtonText({
                                isLoading: markAllAsReadMutation.isPending,
                              })}
                            </div>
                          </div>
                          {!!recentNotificationsLength ? (
                            <NotificationGroupWrapper
                              isNotificationPopover={isNotificationPopover}
                            >
                              <InfiniteList
                                query={recentNotificationsQuery}
                                className={"w-full"}
                              >
                                {recentNotificationsQuery.data?.pages.map(
                                  (page) =>
                                    page.map((notification) => (
                                      <NotificationGroup
                                        key={notification.id}
                                        notification={notification}
                                        setPopoverActive={setPopoverActive}
                                        isNotificationPopover={
                                          isNotificationPopover
                                        }
                                      />
                                    )),
                                )}
                              </InfiniteList>
                            </NotificationGroupWrapper>
                          ) : (
                            <NotificationListEmptyState
                              icon={"BELL_OUTLINE"}
                              title={"No new updates today"}
                              message={"You're all caught up"}
                            />
                          )}
                        </div>
                        <div
                          className={twMerge(
                            "flex w-full flex-col items-start justify-start gap-2",
                          )}
                        >
                          <div className="inline-flex items-center justify-between self-stretch">
                            <div className="justify-end text-sm leading-tight font-normal text-zinc-700">
                              Earlier (
                              {getNotificationCount(olderNotificationsLength)})
                            </div>
                          </div>
                          {!!olderNotificationsLength ? (
                            <NotificationGroupWrapper
                              isNotificationPopover={isNotificationPopover}
                            >
                              <InfiniteList
                                query={olderNotificationsQuery}
                                className={"w-full"}
                              >
                                {olderNotificationsQuery.data?.pages.map(
                                  (page) =>
                                    page.map((notification) => (
                                      <NotificationGroup
                                        key={notification.id}
                                        notification={notification}
                                        setPopoverActive={setPopoverActive}
                                        isNotificationPopover={
                                          isNotificationPopover
                                        }
                                      />
                                    )),
                                )}
                              </InfiniteList>
                            </NotificationGroupWrapper>
                          ) : (
                            <NotificationListEmptyState
                              icon={"BELL_OUTLINE"}
                              title={"No updates"}
                              message={"You're all caught up"}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <NotificationListEmptyState
                        message={"You're all caught up"}
                      />
                    )}
                  </>
                )}
              </>
            }
            tab2={
              <NotificationListAnnouncements
                setPopoverActive={setPopoverActive}
                isNotificationPopover={isNotificationPopover}
              />
            }
          />
        </div>
      </div>
      {children}
    </>
  );
};

const NotificationListAnnouncements = ({
  setPopoverActive,
  isNotificationPopover,
}: {
  setPopoverActive?: (active: boolean) => void;
  isNotificationPopover: boolean;
}) => {
  const { isNonMemberAdmin } = useSellarCurrentBusiness();
  const markAllAsReadMutation = useAnnouncementMarkAllAsRead();
  // Get today's notifications
  const recentAnnouncementsQuery = useAnnouncementList({
    query: [{ type: "today", value: true, modifier: "EQUALS" }],
    limit: 10,
  });

  // Get older notifications (older than today)
  const olderAnnouncementsQuery = useAnnouncementList({
    query: [{ type: "today", value: false, modifier: "EQUALS" }],
    limit: 10,
  });

  if (isNonMemberAdmin) {
    return (
      <NotificationListEmptyState
        icon="BELL_OUTLINE"
        title="Viewing as an admin"
        message="You're viewing this business as an admin. Announcements are only available to members of this business."
      />
    );
  }

  const recentAnnouncementsLength =
    recentAnnouncementsQuery.data?.pages[0]?.length || 0;
  const olderAnnouncementsLength =
    olderAnnouncementsQuery.data?.pages[0]?.length || 0;
  return (
    <>
      {!!recentAnnouncementsLength || !!olderAnnouncementsLength ? (
        <div className="mt-2 flex flex-col items-start justify-start gap-4">
          <div
            className={twMerge(
              "flex w-full flex-col items-start justify-start gap-2",
            )}
          >
            <div className="inline-flex items-center justify-between self-stretch">
              <div className="justify-end text-sm leading-tight font-normal text-zinc-700">
                Today ({getNotificationCount(recentAnnouncementsLength)})
              </div>
              <div
                className="cursor-pointer justify-end text-base leading-normal font-semibold text-zinc-800"
                onClick={() => {
                  markAllAsReadMutation.mutate();
                }}
              >
                {getButtonText({
                  isLoading: markAllAsReadMutation.isPending,
                })}
              </div>
            </div>
            {!!recentAnnouncementsLength ? (
              <NotificationGroupWrapper
                isNotificationPopover={isNotificationPopover}
              >
                <InfiniteList
                  query={recentAnnouncementsQuery}
                  className={"w-full"}
                >
                  {recentAnnouncementsQuery.data?.pages.map((page) =>
                    page.map((announcement) => (
                      <NotificationAnnouncementGroup
                        key={announcement.id}
                        announcement={announcement}
                        setPopoverActive={setPopoverActive}
                        isNotificationPopover={isNotificationPopover}
                      />
                    )),
                  )}
                </InfiniteList>
              </NotificationGroupWrapper>
            ) : (
              <NotificationListEmptyState
                icon={"BELL_OUTLINE"}
                title={"No new updates today"}
                message={"You're all caught up"}
              />
            )}
          </div>
          <div
            className={twMerge(
              "flex w-full flex-col items-start justify-start gap-2",
            )}
          >
            <div className="inline-flex items-center justify-between self-stretch">
              <div className="justify-end text-sm leading-tight font-normal text-zinc-700">
                Earlier ({getNotificationCount(olderAnnouncementsLength)})
              </div>
              {!!olderAnnouncementsLength && (
                <div className="cursor-pointer justify-end text-base leading-normal font-semibold text-zinc-800">
                  {isNotificationPopover && "See all"}
                </div>
              )}
            </div>
            {!!olderAnnouncementsLength ? (
              <NotificationGroupWrapper
                isNotificationPopover={isNotificationPopover}
              >
                <InfiniteList
                  query={olderAnnouncementsQuery}
                  className={"w-full"}
                >
                  {olderAnnouncementsQuery.data?.pages.map((page) =>
                    page.map((announcement) => (
                      <NotificationAnnouncementGroup
                        key={announcement.id}
                        announcement={announcement}
                        setPopoverActive={setPopoverActive}
                        isNotificationPopover={isNotificationPopover}
                      />
                    )),
                  )}
                </InfiniteList>
              </NotificationGroupWrapper>
            ) : (
              <NotificationListEmptyState
                icon={"BELL_OUTLINE"}
                title={"No updates"}
                message={"You're all caught up"}
              />
            )}
          </div>
        </div>
      ) : (
        <NotificationListEmptyState message={"You're all caught up"} />
      )}
    </>
  );
};

const NotificationListEmptyState = ({
  message,
  title,
  icon,
}: {
  message: string;
  title?: string;
  icon?: IconType;
}) => {
  return (
    <div className="inline-flex w-full items-center justify-start gap-4 self-stretch overflow-hidden rounded-lg bg-white p-4">
      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-zinc-100 p-3">
        <Icon icon={icon ?? "MAGNIFY"} />
      </div>
      <div className="inline-flex flex-1 flex-col items-start justify-start">
        <H5 className="">{title || "No new updates"}</H5>
        <p className="flex-1 text-sm font-normal">{message}</p>
      </div>
    </div>
  );
};

const NotificationGroupWrapper = ({
  children,
  isNotificationPopover,
}: {
  children: ReactNode;
  isNotificationPopover: boolean;
}) => {
  return (
    <div
      className={twMerge(
        "flex max-h-[400px] w-full [scrollbar-width:none] flex-col items-start justify-start overflow-y-scroll rounded-lg border border-zinc-300 [::-webkit-scrollbar]:hidden",
        isNotificationPopover && "max-h-[200px]",
      )}
    >
      {children}
    </div>
  );
};
